import { toast } from "@z/ui";
import { TaskDrawer } from "../panes/TaskDrawer";
import type { LayoutTree } from "@hq/contracts";
import { useClient, useStore } from "@hq/client-react";
import {
  closeAllTabs,
  closeOtherTabs,
  closePane,
  findPane,
  LayoutRoot,
  serialize,
  setPaneTitle,
  updatePaneParams,
} from "@hq/layout";
import type { LocalStore } from "@hq/local-store";
import { useRouter } from "@tanstack/react-router";
import { use, useRef, useState, type ReactNode } from "react";
import {
  focusedPane,
  LAYOUT_KV_KEY,
  openSpec,
  pinPane,
  restoreTree,
  unpinPane,
  type OpenOptions,
} from "./model";
import { paneRegistry } from "./registry";
import { routeFor, type PaneSpec } from "./paneTypes";
import { WorkspaceContext, type WorkspaceApi } from "./WorkspaceContext";
import { WorkspaceEmpty } from "./WorkspaceEmpty";

// One load per store, cached so Suspense retries resolve to the same promise.
const initialTrees = new WeakMap<LocalStore, Promise<LayoutTree>>();
function loadTree(store: LocalStore): Promise<LayoutTree> {
  let p = initialTrees.get(store);
  if (!p) {
    p = store.kv.get<string>(LAYOUT_KV_KEY).then(restoreTree);
    initialTrees.set(store, p);
  }
  return p;
}

const PERSIST_DEBOUNCE_MS = 250;

/**
 * The tiling workspace. Owns the layout tree (persisted per device in `kv`, never synced),
 * exposes open/close operations, and keeps the URL on the focused pane.
 */
export function Workspace(props: { children?: ReactNode }) {
  const store = useStore();
  const { taskAutosave } = useClient();
  const [taskDrawer, setTaskDrawer] = useState<{ id: string; open: boolean } | null>(null);
  const taskReturnFocus = useRef<HTMLElement | null>(null);
  const initial = use(loadTree(store));
  const router = useRouter();
  const [tree, setTreeState] = useState(initial);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef(tree);

  const setTree = (
    next: LayoutTree | ((prev: LayoutTree) => LayoutTree),
    history: "push" | "none" = "push",
  ) => {
    const value = typeof next === "function" ? next(latest.current) : next;
    if (value === latest.current) return;
    const before = focusedPane(latest.current);
    const previousRoute = before ? routeFor(before.paneType, before.params) : undefined;
    latest.current = value;
    setTreeState(value);
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(
      () => void store.kv.set(LAYOUT_KV_KEY, serialize(value)),
      PERSIST_DEBOUNCE_MS,
    );
    const focused = focusedPane(value);
    const nextRoute = focused ? routeFor(focused.paneType, focused.params) : undefined;
    // Content can change while a pane keeps its id. Only destination changes are
    // visits; renaming, pinning, resizing, and replaying history must not add entries.
    if (
      history === "push" &&
      nextRoute &&
      nextRoute !== previousRoute &&
      nextRoute !== router.history.location.pathname
    ) {
      void router.navigate({ to: nextRoute });
    }
  };

  const openTask = (taskId: string) => {
    const active = document.activeElement;
    taskReturnFocus.current =
      active instanceof HTMLElement
        ? (active.closest<HTMLElement>("[data-task-row]") ?? active)
        : null;
    setTaskDrawer({ id: taskId, open: true });
  };
  const closeTask = async () => {
    if (!taskDrawer) return true;
    await taskAutosave.flush(taskDrawer.id);
    if (taskAutosave.getState(taskDrawer.id) === "error") {
      toast({
        title: "Could not save description",
        description: "Your draft is still here. Try again before closing.",
        variant: "danger",
      });
      return false;
    }
    setTaskDrawer((current) => (current ? { ...current, open: false } : null));
    const focused = focusedPane(latest.current);
    // Older saved layouts may still contain a full-page task pane.
    if (focused?.paneType === "task") {
      setTree((current) => openSpec(closePane(current, focused.id), { paneType: "inbox" }));
    } else if (focused) {
      void router.navigate({ to: routeFor(focused.paneType, focused.params), replace: true });
    }
    return true;
  };

  const api: WorkspaceApi = {
    tree,
    setTree,
    restoreRoute: (spec) => {
      if (spec.paneType === "task") openTask(spec.taskId);
      else {
        setTaskDrawer((current) => (current ? { ...current, open: false } : null));
        // The router already selected this history entry. Replaying it is a
        // layout update, not another navigation (which would erase Forward).
        setTree((t) => openSpec(t, spec), "none");
      }
    },
    open: (spec: PaneSpec, opts?: OpenOptions) => {
      if (spec.paneType === "task") openTask(spec.taskId);
      else {
        setTaskDrawer((current) => (current ? { ...current, open: false } : null));
        setTree((t) => openSpec(t, spec, opts));
      }
    },
    openNote: (noteId, opts = {}) =>
      setTree((t) =>
        openSpec(
          t,
          opts.autoFocus
            ? { paneType: "note", noteId, autoFocus: true }
            : { paneType: "note", noteId },
          opts.pinned ? { mode: "tab", pinned: true } : { mode: "replace", preview: true },
        ),
      ),
    openTask,
    close: (paneId) => setTree((t) => closePane(t, paneId)),
    closeOthers: (paneId) => setTree((t) => closeOtherTabs(t, paneId)),
    closeGroup: (paneId) =>
      setTree((t) => {
        const group = findPane(t, paneId)?.group;
        return group ? closeAllTabs(t, group.id) : t;
      }),
    pin: (paneId) => setTree((t) => pinPane(t, paneId)),
    unpin: (paneId) => setTree((t) => unpinPane(t, paneId)),
    setTitle: (paneId, title) => setTree((t) => setPaneTitle(t, paneId, title)),
    setParams: (paneId, params) => setTree((t) => updatePaneParams(t, paneId, params)),
    focused: focusedPane(tree),
  };

  return (
    <WorkspaceContext.Provider value={api}>
      {props.children}
      <LayoutRoot
        keyboard={false}
        tree={tree}
        onChange={setTree}
        registry={paneRegistry}
        emptyState={<WorkspaceEmpty />}
        onTabDoubleClick={(pane) => api.pin(pane.id)}
        className="h-full min-h-0 flex-1"
      />
      {taskDrawer && (
        <TaskDrawer
          taskId={taskDrawer.id}
          open={taskDrawer.open}
          onClose={closeTask}
          returnFocus={taskReturnFocus}
        />
      )}
    </WorkspaceContext.Provider>
  );
}
